import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Divider
} from '@mui/material';
import { Settings as SettingsIcon, Send as SendIcon, DeleteForever as DeleteIcon, Storage as StorageIcon } from '@mui/icons-material';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Settings = () => {
    const { isAdmin } = useAuth();
    const [settings, setSettings] = useState({
        provider: 'free',
        model: '',
        api_key: '',
        base_url: '',
        temperature: 0.3,
        max_tokens: 1000
    });
    const [settingsLoadedFromDB, setSettingsLoadedFromDB] = useState(false); // Track if settings were loaded from DB
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [testPrompt, setTestPrompt] = useState('How much is 3 * 8 ?');
    const [testResponse, setTestResponse] = useState('');
    const [testing, setTesting] = useState(false);

    // Database management state
    const [eshops, setEshops] = useState([]);
    const [selectedEshopSource, setSelectedEshopSource] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState('eshop'); // 'eshop' or 'all'
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadSettings();
        loadEshops();
    }, []);

    const loadSettings = async () => {
        try {
            console.log('Loading settings from API'); // Debug log
            const response = await api.get('/api/llm-settings');
            console.log('Settings loaded from API:', response.data); // Debug log
            // The backend now returns an object with 'exists' flag and settings
            // Extract the settings properties, excluding the 'exists' flag
            const { exists, ...settingsData } = response.data;
            setSettings(settingsData);
            // Check if settings were actually retrieved from the database
            setSettingsLoadedFromDB(exists);
        } catch (error) {
            console.error('Error loading settings:', error); // Debug log
            if (error.response?.status === 404) {
                // If no settings exist, use default values
                setSettingsLoadedFromDB(false);
            } else {
                setMessage({
                    type: 'error',
                    text: `Error loading settings: ${error.response?.data?.detail || error.message}`
                });
                setSettingsLoadedFromDB(false);
            }
        } finally {
            setLoading(false);
        }
    };

    const loadEshops = async () => {
        try {
            const response = await api.get('/api/eshops');
            setEshops(response.data);
        } catch (error) {
            console.error('Error loading eshops:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNumberInputChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: name === 'temperature' ?
                (value === '' ? 0.3 : parseFloat(value)) :
                (value === '' ? 1000 : parseInt(value))
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            console.log('Attempting to save settings:', settings); // Debug log
            console.log('Settings loaded from DB:', settingsLoadedFromDB); // Debug log

            // If settings were loaded from the database (not just defaults), update them; otherwise create new ones
            if (settingsLoadedFromDB) {
                console.log('Updating existing settings with ID:', settings.id); // Debug log
                await api.put('/api/llm-settings', {
                    provider: settings.provider,
                    model: settings.model,
                    api_key: settings.api_key || null,
                    base_url: settings.base_url || null,
                    temperature: settings.temperature,
                    max_tokens: settings.max_tokens
                });
            } else {
                console.log('Creating new settings'); // Debug log
                await api.post('/api/llm-settings', {
                    provider: settings.provider,
                    model: settings.model,
                    api_key: settings.api_key || null,
                    base_url: settings.base_url || null,
                    temperature: settings.temperature,
                    max_tokens: settings.max_tokens
                });
            }

            // Reload settings to get the updated values (including the ID if it was created)
            await loadSettings();
            setMessage({ type: 'success', text: 'Settings saved successfully!' });
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({
                type: 'error',
                text: `Error saving settings: ${error.response?.data?.detail || error.message}`
            });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setSettings({
            provider: 'free',
            model: 'qwen3',
            api_key: '',
            base_url: '',
            temperature: 0.3,
            max_tokens: 1000
        });
        setMessage({ type: 'info', text: 'Settings reset to defaults' });
    };

    const handleTest = async () => {
        if (!testPrompt.trim()) {
            setMessage({ type: 'error', text: 'Please enter a test prompt' });
            return;
        }

        setTesting(true);
        setTestResponse('');
        setMessage({ type: '', text: '' });

        try {
            const response = await api.post('/api/test-llm', {
                prompt: testPrompt,
                settings: {
                    provider: settings.provider,
                    model: settings.model,
                    api_key: settings.api_key || null,
                    base_url: settings.base_url || null,
                    temperature: settings.temperature,
                    max_tokens: settings.max_tokens
                }
            });

            setTestResponse(response.data.response);
            setMessage({ type: 'success', text: 'Test completed successfully!' });
        } catch (error) {
            setMessage({
                type: 'error',
                text: `Error testing LLM: ${error.response?.data?.detail || error.message}`
            });
        } finally {
            setTesting(false);
        }
    };

    const handleDeleteClick = (type) => {
        if (type === 'eshop' && !selectedEshopSource) {
            setMessage({ type: 'error', text: 'Please select an e-shop to delete products from' });
            return;
        }
        setDeleteType(type);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        setDeleting(true);
        setDeleteDialogOpen(false);
        setMessage({ type: '', text: '' });

        try {
            let response;
            if (deleteType === 'all') {
                response = await api.delete('/api/database/products/all');
            } else {
                response = await api.delete(`/api/database/products/eshop/${selectedEshopSource}`);
            }
            setMessage({ type: 'success', text: response.data.message });
            if (deleteType === 'eshop') setSelectedEshopSource('');
        } catch (error) {
            setMessage({
                type: 'error',
                text: `Error deleting products: ${error.response?.data?.detail || error.message}`
            });
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', pb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SettingsIcon sx={{ mr: 1, fontSize: 32 }} />
                <Typography variant="h4">Settings</Typography>
            </Box>

            {!isAdmin && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    You do not have admin privileges to modify system settings. Please contact an administrator.
                </Alert>
            )}

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ mr: 1 }} /> LLM Configuration
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="provider-label">Provider</InputLabel>
                            <Select
                                labelId="provider-label"
                                id="provider"
                                name="provider"
                                value={settings.provider || 'free'}
                                onChange={handleInputChange}
                                label="Provider"
                                disabled={!isAdmin}
                            >
                                <MenuItem value="free">Free (Groq)</MenuItem>
                                <MenuItem value="qwen">Qwen</MenuItem>
                                <MenuItem value="openai">OpenAI</MenuItem>
                                <MenuItem value="groq">Groq</MenuItem>
                                <MenuItem value="ollama">Ollama</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Model"
                            name="model"
                            value={settings.model}
                            onChange={handleInputChange}
                            variant="outlined"
                            helperText="The model to use for LLM operations (e.g., qwen3, gpt-4, llama3)"
                            disabled={!isAdmin}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="API Key"
                            name="api_key"
                            type="password"
                            value={settings.api_key}
                            onChange={handleInputChange}
                            variant="outlined"
                            helperText="Your API key for the selected provider (leave empty to use default)"
                            disabled={!isAdmin}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Base URL"
                            name="base_url"
                            value={settings.base_url}
                            onChange={handleInputChange}
                            variant="outlined"
                            helperText="Base URL for the LLM API (leave empty to use default)"
                            disabled={!isAdmin}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Temperature"
                            name="temperature"
                            type="number"
                            inputProps={{ step: 0.1, min: 0, max: 1 }}
                            value={settings.temperature}
                            onChange={handleNumberInputChange}
                            variant="outlined"
                            helperText="Controls randomness (0.0-1.0)"
                            disabled={!isAdmin}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Max Tokens"
                            name="max_tokens"
                            type="number"
                            inputProps={{ min: 1, max: 10000 }}
                            value={settings.max_tokens}
                            onChange={handleNumberInputChange}
                            variant="outlined"
                            helperText="Max tokens to generate"
                            disabled={!isAdmin}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={saving || !isAdmin}
                        startIcon={saving ? <CircularProgress size={20} /> : null}
                    >
                        {saving ? 'Saving...' : 'Save LLM Settings'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleReset}
                        disabled={saving || !isAdmin}
                    >
                        Reset to Defaults
                    </Button>
                </Box>

                {/* Test LLM Section */}
                <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Test LLM Connection
                    </Typography>

                    <TextField
                        fullWidth
                        label="Test Prompt"
                        value={testPrompt}
                        onChange={(e) => setTestPrompt(e.target.value)}
                        variant="outlined"
                        multiline
                        rows={2}
                        sx={{ mb: 2, mt: 1 }}
                        disabled={!isAdmin}
                    />

                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleTest}
                        disabled={testing || saving || !isAdmin}
                        startIcon={testing ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {testing ? 'Testing...' : 'Test Connection'}
                    </Button>

                    {testResponse && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Response:</Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {testResponse}
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Database Management Section */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <StorageIcon sx={{ mr: 1 }} /> Database Management
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Delete Products by E-shop
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Permanently remove all scraped products associated with a specific e-shop.
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Select E-shop</InputLabel>
                                <Select
                                    value={selectedEshopSource}
                                    onChange={(e) => setSelectedEshopSource(e.target.value)}
                                    label="Select E-shop"
                                    disabled={!isAdmin || deleting}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {eshops.map((eshop) => (
                                        <MenuItem key={eshop.id} value={eshop.eshop_id}>
                                            {eshop.name} ({eshop.eshop_id})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="error"
                                startIcon={deleting && deleteType === 'eshop' ? <CircularProgress size={20} /> : <DeleteIcon />}
                                onClick={() => handleDeleteClick('eshop')}
                                disabled={!isAdmin || deleting || !selectedEshopSource}
                            >
                                Delete Products
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        Truncate Database
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Permanently remove <strong>ALL</strong> products from the database. This action cannot be undone.
                    </Typography>
                    
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={deleting && deleteType === 'all' ? <CircularProgress size={20} /> : <DeleteIcon />}
                        onClick={() => handleDeleteClick('all')}
                        disabled={!isAdmin || deleting}
                    >
                        Delete All Products (Truncate)
                    </Button>
                </Box>
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
                    <DeleteIcon sx={{ mr: 1 }} /> Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteType === 'all' 
                            ? "Are you sure you want to delete ALL products from the database? This action is permanent and cannot be undone."
                            : `Are you sure you want to delete all products from e-shop "${eshops.find(e => e.eshop_id === selectedEshopSource)?.name || selectedEshopSource}"? This action is permanent and cannot be undone.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" autoFocus disabled={deleting}>
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Settings;